import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import api, { apiError, rupiah } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";

const POSITIONS = ["Operator Laundry", "Host Live", "Teknisi", "Pickup Delivery"];
const empty = { name: "", username: "", password: "", phone: "", position: "Operator Laundry", outlet_id: "", daily_wage: 100000, status: "aktif", start_date: "" };

export default function KaryawanTab() {
  const [users, setUsers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [u, o] = await Promise.all([api.get("/users"), api.get("/outlets")]);
      setUsers(u.data); setOutlets(o.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm({ ...empty, outlet_id: outlets[0]?.id || "" }); setEditing(null); setOpen(true); };
  const openEdit = (u) => {
    setForm({ name: u.name, username: u.username, password: "", phone: u.phone || "", position: u.position || "Kasir",
      outlet_id: u.outlet_id || "", daily_wage: u.daily_wage || 0, status: u.status, start_date: u.start_date ? String(u.start_date).slice(0, 10) : "" });
    setEditing(u); setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, daily_wage: Number(form.daily_wage) };
      if (editing) await api.put(`/users/${editing.id}`, payload);
      else await api.post("/users", payload);
      toast.success("Karyawan disimpan");
      setOpen(false); await load();
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("Karyawan ini sudah tidak ada (mungkin telah dihapus). Daftar diperbarui.");
        setOpen(false); await load();
      } else {
        toast.error(apiError(e));
      }
    }
    finally { setSaving(false); }
  };

  const toggleStatus = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { name: u.name, username: u.username, phone: u.phone, position: u.position,
        outlet_id: u.outlet_id, daily_wage: u.daily_wage, start_date: u.start_date, status: u.status === "aktif" ? "nonaktif" : "aktif" });
      toast.success("Status diperbarui"); await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Hapus karyawan "${u.name}" (@${u.username})? Data absensi & jadwalnya ikut terhapus permanen.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Karyawan dihapus"); await load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#3368A0]" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">Data Karyawan</h2>
        <Button data-testid="add-karyawan-button" onClick={openAdd} className="bg-[#3368A0] hover:bg-[#284f7a] rounded-xl">
          <Plus className="h-4 w-4 mr-1.5" /> Tambah Karyawan
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="karyawan-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-5 py-3">Nama</th>
              <th className="text-left px-5 py-3">Jabatan</th>
              <th className="text-left px-5 py-3">Outlet</th>
              <th className="text-left px-5 py-3">Masa Kerja</th>
              <th className="text-right px-5 py-3">Gaji Harian</th>
              <th className="text-right px-5 py-3">Total Gaji Bulan Ini</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100">
                <td className="px-5 py-3">
                  <p className="font-medium text-slate-700">{u.name}</p>
                  <p className="text-xs text-slate-400">@{u.username}</p>
                </td>
                <td className="px-5 py-3 text-slate-500">{u.position}</td>
                <td className="px-5 py-3 text-slate-500">{u.outlet_name}</td>
                <td className="px-5 py-3 text-slate-500">{u.masa_kerja || "-"}</td>
                <td className="px-5 py-3 text-right font-mono text-slate-600">{rupiah(u.daily_wage)}</td>
                <td className="px-5 py-3 text-right font-mono font-semibold text-emerald-700">{rupiah(u.total_gaji_bulan_ini)}</td>
                <td className="px-5 py-3"><StatusBadge status={u.status} /></td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(u)} data-testid={`edit-karyawan-${u.id}`} className="text-[#3368A0] hover:text-sky-800 p-1.5"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => toggleStatus(u)} data-testid={`toggle-karyawan-${u.id}`}
                    className="text-xs font-medium text-slate-500 hover:text-slate-800 px-2">
                    {u.status === "aktif" ? "Nonaktifkan" : "Aktifkan"}
                  </button>
                  <button onClick={() => remove(u)} data-testid={`delete-karyawan-${u.id}`} className="text-rose-500 hover:text-rose-700 p-1.5"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Karyawan" : "Tambah Karyawan"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>Nama Lengkap</Label>
              <Input data-testid="karyawan-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Username</Label>
                <Input data-testid="karyawan-username-input" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Password {editing && <span className="text-xs text-slate-400">(kosong = tetap)</span>}</Label>
                <Input data-testid="karyawan-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nomor HP</Label>
                <Input data-testid="karyawan-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Jabatan</Label>
                <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
                  <SelectTrigger data-testid="karyawan-position-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Outlet</Label>
                <Select value={form.outlet_id} onValueChange={(v) => setForm({ ...form, outlet_id: v })}>
                  <SelectTrigger data-testid="karyawan-outlet-select"><SelectValue placeholder="Pilih outlet" /></SelectTrigger>
                  <SelectContent>{outlets.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Gaji Harian (Rp)</Label>
                <Input data-testid="karyawan-wage-input" type="number" value={form.daily_wage} onChange={(e) => setForm({ ...form, daily_wage: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Tanggal Mulai Bekerja</Label>
              <Input data-testid="karyawan-start-date-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              <p className="text-xs text-slate-400">Untuk menghitung lama masa kerja karyawan.</p></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="save-karyawan-button" onClick={save} disabled={saving} className="bg-[#3368A0] hover:bg-[#284f7a]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
